import { useRouter } from 'next/router';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getLocationsSelect } from '../services/locations';

const UserContext = createContext<UseUser | null>(null);
UserContext.displayName = 'UserContext';

type SetPreferredLocation = (location: number) => void;
type SetLastSelectedLocation = (location: number) => void;
type SetFirstVisit = () => void;
type SetLatLng = (latitude: number, longitude: number) => void;
type SetFlag = (flag: string) => void;
type SetTheme = (theme: string) => void;

export const isRemoveVenueParam = (param?: string | string[] | null): boolean => {
  if (!param) return false;
  const val = (Array.isArray(param) ? param[0] : param)?.trim().toLowerCase();
  return val === 'null' || val === '0';
};

type UseUser = {
  preferredLocation?: number;
  lastSelectedLocation?: number;
  firstVisit?: number;
  latitude?: number;
  longitude?: number;
  flags?: string[];
  theme?: string;
  setPreferredLocation: SetPreferredLocation;
  setLastSelectedLocation: SetLastSelectedLocation;
  clearVenue: () => void;
  setLatLng: SetLatLng;
  setFlag: SetFlag;
  setTheme: SetTheme;
  loaded: boolean;
};

export const useUserContext = (): UseUser => useContext(UserContext) as UseUser;

type UserData = {
  preferredLocation?: {
    value: number;
    expires: number;
  };
  lastSelectedLocation?: {
    value: number;
    expires: number;
  };
  firstVisit?: {
    value: number;
    expires: number;
  };
  latitude?: {
    value: number;
    expires: number;
  };
  longitude?: {
    value: number;
    expires: number;
  };
  theme?: {
    value: string;
    expires: number;
  };
  flags?: string[];
};

const isAppModeCookieTrue = (cookieName: string): boolean => {
  if (typeof document === 'undefined') return false;
  const nameEQ = cookieName + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length) === 'true';
  }
  return false;
};

export const UserProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const prevUrlVenueRef = useRef<string | undefined>(undefined);
  const [mounted, setMounted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [user, setUser] = useState<UserData>({});

  const setUserValue = useCallback(
    <K extends keyof Omit<UserData, 'flags'> = keyof Omit<UserData, 'flags'>>(
      key: K,
      value: Required<UserData>[K]['value'],
      expires = 0,
    ): void => {
      setUser((user) => ({
        ...user,
        [key]: { value, expires: expires === 0 ? 0 : Date.now() + expires },
      }));
    },
    [],
  );

  const setFlag = (flag: string): void => {
    const flags = user.flags ?? [];
    if (!flags.includes(flag)) {
      flags.push(flag);
      setUser((user) => ({
        ...user,
        flags: flags,
      }));
    }
  };

  const getUserValue = useCallback(
    <K extends keyof Omit<UserData, 'flags'> = keyof Omit<UserData, 'flags'>>(
      key: K,
    ): Required<UserData>[K]['value'] | undefined => {
      const userProp = user[key];

      if (!userProp) return undefined;
      if (userProp.expires > 0 && userProp.expires < Date.now()) return undefined;

      return userProp.value;
    },
    [user],
  );

  const getUserFlags = (): string[] => {
    return user.flags ?? [];
  };

  const clearVenue = useCallback((): void => {
    setUser((prevUser) => {
      const nextUser = { ...prevUser };
      delete nextUser.lastSelectedLocation;
      delete nextUser.preferredLocation;
      return nextUser;
    });
  }, []);

  const setPreferredLocation: SetPreferredLocation = useCallback(
    (location) => {
      if (!location) {
        setUser((prevUser) => {
          const nextUser = { ...prevUser };
          delete nextUser.preferredLocation;
          return nextUser;
        });
      } else {
        setUserValue('preferredLocation', location, 0);
      }
    },
    [setUserValue],
  );

  const setLastSelectedLocation: SetLastSelectedLocation = useCallback(
    (location) => {
      if (!location) {
        clearVenue();
      } else {
        setUserValue('lastSelectedLocation', location, 0);
      }
    },
    [setUserValue, clearVenue],
  );

  const setFirstVisit: SetFirstVisit = useCallback(() => {
    setUserValue('firstVisit', Date.now(), 0);
  }, [setUserValue]);

  const setLatLng: SetLatLng = useCallback(
    (latitude, longitude) => {
      setUserValue('latitude', latitude, 15 * 60 * 1000);
      setUserValue('longitude', longitude, 15 * 60 * 1000);
    },
    [setUserValue],
  );

  const setTheme: SetTheme = useCallback(
    (theme) => {
      setUserValue('theme', theme, 0);
    },
    [setUserValue],
  );

  useEffect(() => {
    window.setAppTheme = setTheme;

    return (): void => {
      delete window.setAppTheme;
    };
  }, [setTheme]);

  useEffect(() => {
    const prefersDarkMode = window.matchMedia('(prefers-color-scheme:dark)').matches;
    if (!mounted) {
      if (typeof document !== 'undefined') {
        const isAppMode = isAppModeCookieTrue('app_mode');

        let initialUser: UserData = {};
        if (isAppMode) {
          setFirstVisit();
          setTheme(prefersDarkMode ? 'dark' : '');
        } else {
          try {
            initialUser = JSON.parse(localStorage.getItem('tg-user') || '').value as UserData;
            if (!initialUser.firstVisit) {
              setFirstVisit();
              setTheme(prefersDarkMode ? 'dark' : '');
            }
          } catch {
            initialUser = {};
            setFirstVisit();
            setTheme(prefersDarkMode ? 'dark' : '');
          }
        }

        const searchParams = new URLSearchParams(window.location.search);
        const urlVenueParam = (searchParams.get('venue') || searchParams.get('venue_id'))?.trim();

        if (urlVenueParam) {
          prevUrlVenueRef.current = urlVenueParam;
          if (isRemoveVenueParam(urlVenueParam)) {
            delete initialUser.lastSelectedLocation;
            delete initialUser.preferredLocation;
            if (!isAppMode) {
              localStorage.setItem('tg-user', JSON.stringify({ value: initialUser }));
            }
          } else {
            getLocationsSelect({ lookup: false })
              .then((res) => {
                const venues = Object.values(res.data ?? {});
                const match = venues.find((loc) => {
                  const locVenueId = loc.venue_id ?? loc.site_id;
                  return (
                    (locVenueId !== undefined && String(locVenueId) === urlVenueParam) ||
                    loc.alias?.toLowerCase() === urlVenueParam.toLowerCase() ||
                    String(loc.id) === urlVenueParam
                  );
                });
                if (match?.id) {
                  setLastSelectedLocation(match.id);
                }
              })
              .catch(() => {});
          }
        }

        setUser(initialUser);
        setLoaded(true);
      }

      setMounted(true);
    }
    const html = document.getElementsByTagName('html');
    html[0].classList.forEach((c) => {
      html[0].classList.remove(c);
    });
    const theme = getUserValue('theme');
    if (theme) {
      html[0].classList.add(theme);
    }
  }, [mounted, setFirstVisit, setTheme, getUserValue, setLastSelectedLocation]);

  useEffect(() => {
    if (!router.isReady) return;

    const currentVenueParam = (router.query.venue || router.query.venue_id) as
      string | string[] | undefined;
    const venueParam = (
      Array.isArray(currentVenueParam) ? currentVenueParam[0] : currentVenueParam
    )?.trim();

    if (venueParam) {
      if (venueParam !== prevUrlVenueRef.current) {
        prevUrlVenueRef.current = venueParam;
        if (isRemoveVenueParam(venueParam)) {
          clearVenue();
          if (router.pathname.includes('[venue]')) {
            const country = router.query.country || 'us';
            const cleanPath = router.pathname
              .replace('/[venue]', '')
              .replace('[country]', String(country));
            router.push(cleanPath ? `${cleanPath}/` : `/${country}/`);
          }
          return;
        }
        getLocationsSelect({ lookup: false })
          .then((res) => {
            const venues = Object.values(res.data ?? {});
            const match = venues.find((loc) => {
              const locVenueId = loc.venue_id ?? loc.site_id;
              return (
                (locVenueId !== undefined && String(locVenueId) === venueParam) ||
                loc.alias?.toLowerCase() === venueParam.toLowerCase() ||
                String(loc.id) === venueParam
              );
            });
            if (match?.id) {
              setLastSelectedLocation(match.id);
            }
          })
          .catch(() => {});
      } else if (isRemoveVenueParam(venueParam)) {
        if (router.pathname.includes('[venue]')) {
          const country = router.query.country || 'us';
          const cleanPath = router.pathname
            .replace('/[venue]', '')
            .replace('[country]', String(country));
          router.push(cleanPath ? `${cleanPath}/` : `/${country}/`);
        }
      }
    } else {
      prevUrlVenueRef.current = undefined;
    }
  }, [
    router.isReady,
    router.query.venue,
    router.query.venue_id,
    router.pathname,
    router.query.country,
    router,
    setLastSelectedLocation,
    clearVenue,
  ]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const isAppMode = isAppModeCookieTrue('app_mode');

      if (!isAppMode) {
        localStorage.setItem('tg-user', JSON.stringify({ value: user }));
      }
    }
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        preferredLocation: getUserValue('preferredLocation'),
        lastSelectedLocation: getUserValue('lastSelectedLocation'),
        firstVisit: getUserValue('firstVisit'),
        latitude: getUserValue('latitude'),
        longitude: getUserValue('longitude'),
        theme: getUserValue('theme'),
        flags: getUserFlags(),
        setPreferredLocation,
        setLastSelectedLocation,
        clearVenue,
        setLatLng,
        setFlag,
        setTheme,
        loaded,
      }}>
      {children}
    </UserContext.Provider>
  );
};
